/**
 * DocumentPreview Component
 *
 * Modal component to preview document with:
 * - Document image/PDF viewer
 * - Extracted OCR text
 * - AI analysis results
 * - Verification checks (pass/fail)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Download,
  RefreshCw,
  FileText,
  Image,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Sparkles,
  Shield,
  Loader2,
  ExternalLink,
  ZoomIn,
  ZoomOut,
  RotateCw
} from 'lucide-react';
import { useDocument, useDownloadDocument, useAnalyzeDocument, useDocumentPolling } from '../hooks/useDocuments';
import { useToast } from '../contexts/ToastContext';
import { Button, Badge } from './ui';
import { cn } from '../lib/utils';

// Status configuration
const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'muted', icon: Sparkles },
  processing: { label: 'Processing', color: 'info', icon: Loader2, animate: true },
  verified: { label: 'Verified', color: 'success', icon: CheckCircle2 },
  failed: { label: 'Failed', color: 'danger', icon: XCircle },
  rejected: { label: 'Rejected', color: 'danger', icon: XCircle },
  pending_review: { label: 'Needs Review', color: 'warning', icon: AlertTriangle },
};

// Document type labels
const DOCUMENT_TYPE_LABELS = {
  passport: 'Passport',
  driver_license: "Driver's License",
  id_card: 'ID Card',
  utility_bill: 'Proof of Address',
  bank_statement: 'Bank Statement',
  proof_of_address: 'Proof of Address',
  government_letter: 'Government Letter',
  tax_document: 'Tax Document',
  selfie: 'Selfie',
  other: 'Other Document',
};

/**
 * Format date for display
 */
function formatDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format file size
 */
function formatFileSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Verification check item
 */
function VerificationCheck({ label, passed, details }) {
  return (
    <div className={cn(
      'flex items-start gap-2.5 p-2.5 rounded-lg mb-2 last:mb-0',
      passed
        ? 'bg-green-500/10 text-green-600 dark:text-green-400'
        : 'bg-red-500/10 text-red-600 dark:text-red-400'
    )}>
      {passed ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
      <div className="flex-1">
        <span className="block text-[13px] font-medium">{label}</span>
        {details && <span className="block text-xs opacity-80 mt-0.5">{details}</span>}
      </div>
    </div>
  );
}

export default function DocumentPreview({ documentId, document: initialDocument, isOpen, onClose }) {
  const toast = useToast();

  // State
  const [previewUrl, setPreviewUrl] = useState(null);
  const [imageZoom, setImageZoom] = useState(1);
  const [imageRotation, setImageRotation] = useState(0);
  const [activeTab, setActiveTab] = useState('preview');

  // Fetch document data if not provided
  const { data: fetchedDocument, isLoading: isLoadingDocument } = useDocument(
    initialDocument ? null : documentId,
    { enabled: isOpen && !initialDocument }
  );

  const document = initialDocument || fetchedDocument;

  // Poll for processing updates
  const { data: polledDocument } = useDocumentPolling(
    document?.status === 'processing' ? document.id : null,
    {
      enabled: isOpen && document?.status === 'processing',
      onComplete: () => {
        toast.success('Document processing complete');
      },
    }
  );

  // Use polled document if available
  const currentDocument = polledDocument || document;

  // Mutations
  const downloadMutation = useDownloadDocument();
  const analyzeMutation = useAnalyzeDocument();

  // Fetch preview URL when document is available
  useEffect(() => {
    if (!currentDocument || !isOpen) {
      setPreviewUrl(null);
      return;
    }

    // If document has a storage URL, use it
    if (currentDocument.storage_url) {
      setPreviewUrl(currentDocument.storage_url);
    }
  }, [currentDocument, isOpen]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setImageZoom(1);
      setImageRotation(0);
      setActiveTab('preview');
    }
  }, [isOpen]);

  // Handle close with escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Handle download
  const handleDownload = useCallback(async () => {
    if (!currentDocument) return;

    try {
      await downloadMutation.mutateAsync({
        id: currentDocument.id,
        openInNewTab: false,
        filename: currentDocument.file_name || currentDocument.filename,
      });
      toast.success('Download started');
    } catch (err) {
      toast.error(`Download failed: ${err.message}`);
    }
  }, [currentDocument, downloadMutation, toast]);

  // Handle re-analyze
  const handleReanalyze = useCallback(async () => {
    if (!currentDocument) return;

    try {
      await analyzeMutation.mutateAsync({ id: currentDocument.id });
      toast.success('AI analysis started');
    } catch (err) {
      toast.error(`Analysis failed: ${err.message}`);
    }
  }, [currentDocument, analyzeMutation, toast]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    setImageZoom((z) => Math.min(z + 0.25, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setImageZoom((z) => Math.max(z - 0.25, 0.5));
  }, []);

  const handleRotate = useCallback(() => {
    setImageRotation((r) => (r + 90) % 360);
  }, []);

  // Handle backdrop click
  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  if (!isOpen) return null;

  // Loading state
  if (isLoadingDocument) {
    return (
      <div
        className="fixed inset-0 bg-black/80 flex items-center justify-content z-[1000] p-6"
        onClick={handleBackdropClick}
      >
        <div className="flex flex-col items-center justify-center gap-4 text-white text-center">
          <Loader2 size={32} className="animate-spin" />
          <span>Loading document...</span>
        </div>
      </div>
    );
  }

  // No document
  if (!currentDocument) {
    return (
      <div
        className="fixed inset-0 bg-black/80 flex items-center justify-center z-[1000] p-6"
        onClick={handleBackdropClick}
      >
        <div className="flex flex-col items-center justify-center gap-4 text-white text-center">
          <AlertTriangle size={32} />
          <span>Document not found</span>
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      </div>
    );
  }

  const status = STATUS_CONFIG[currentDocument.status] || STATUS_CONFIG.pending;
  const StatusIcon = status.icon;
  const typeLabel = DOCUMENT_TYPE_LABELS[currentDocument.document_type] || currentDocument.document_type;
  const isImage = currentDocument.content_type?.startsWith('image/');

  // Determine verification checks
  const verificationChecks = [];

  if (currentDocument.mrz_valid !== undefined) {
    verificationChecks.push({
      label: 'MRZ Validation',
      passed: currentDocument.mrz_valid,
      details: currentDocument.mrz_valid ? 'Checksums verified' : 'Invalid checksums detected',
    });
  }

  if (currentDocument.ocr_confidence !== undefined) {
    verificationChecks.push({
      label: 'OCR Quality',
      passed: currentDocument.ocr_confidence >= 70,
      details: `${Math.round(currentDocument.ocr_confidence)}% confidence`,
    });
  }

  if (currentDocument.extracted_data?._forensics?.ela?.status === 'completed') {
    const ela = currentDocument.extracted_data._forensics.ela;
    verificationChecks.push({
      label: 'Image Forensics (ELA)',
      passed: ela.passed,
      details: `Score: ${ela.manipulation_score}/100`,
    });
  }

  if (currentDocument.extracted_data?._forensics?.synthetic?.status === 'completed') {
    const syn = currentDocument.extracted_data._forensics.synthetic;
    verificationChecks.push({
      label: 'Synthetic Detection',
      passed: syn.passed,
      details: syn.is_synthetic ? `AI-generated (${syn.confidence}%)` : 'Authentic image',
    });
  }

  if (currentDocument.fraud_signals) {
    verificationChecks.push({
      label: 'Fraud Detection',
      passed: currentDocument.fraud_signals.length === 0,
      details: currentDocument.fraud_signals.length > 0
        ? `${currentDocument.fraud_signals.length} signal(s) detected`
        : 'No fraud signals',
    });
  }

  if (currentDocument.status === 'verified') {
    verificationChecks.push({
      label: 'Overall Status',
      passed: true,
      details: 'Document verified',
    });
  } else if (currentDocument.status === 'failed' || currentDocument.status === 'rejected') {
    verificationChecks.push({
      label: 'Overall Status',
      passed: false,
      details: currentDocument.rejection_reason || 'Verification failed',
    });
  }

  // Status variant mapping
  const statusVariants = {
    success: 'success',
    danger: 'destructive',
    warning: 'warning',
    info: 'info',
    muted: 'secondary',
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-[1000] p-6"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white dark:bg-grey-900 rounded-2xl overflow-hidden w-full max-w-[1200px] max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-4 px-5 py-4 border-b border-grey-200 dark:border-grey-700 bg-grey-50 dark:bg-grey-800">
          <div className="w-12 h-12 rounded-xl bg-grey-100 dark:bg-grey-700 flex items-center justify-center text-grey-500">
            {isImage ? <Image size={24} /> : <FileText size={24} />}
          </div>

          <div className="flex-1">
            <div className="text-lg font-semibold mb-1">{typeLabel}</div>
            <div className="text-[13px] text-grey-500">
              {currentDocument.file_name || currentDocument.filename || 'Unnamed document'}
              {' • '}
              {formatFileSize(currentDocument.file_size)}
              {' • '}
              Uploaded {formatDate(currentDocument.uploaded_at || currentDocument.created_at)}
            </div>
          </div>

          <Badge variant={statusVariants[status.color] || 'secondary'} className="flex items-center gap-1.5 px-3.5 py-2 rounded-full">
            <StatusIcon size={14} className={status.animate ? 'animate-spin' : ''} />
            {status.label}
          </Badge>

          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleReanalyze}
              disabled={analyzeMutation.isPending || currentDocument.status === 'processing'}
              title="Re-run AI analysis"
            >
              {analyzeMutation.isPending ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <RefreshCw size={18} />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDownload}
              disabled={downloadMutation.isPending}
              title="Download document"
            >
              {downloadMutation.isPending ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Download size={18} />
              )}
            </Button>
          </div>

          <Button variant="ghost" size="icon" onClick={onClose} title="Close">
            <X size={20} />
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-5 py-3 bg-grey-50 dark:bg-grey-800 border-b border-grey-200 dark:border-grey-700">
          {[
            { key: 'preview', label: 'Preview' },
            { key: 'extracted', label: 'Extracted Data' },
            { key: 'analysis', label: 'AI Analysis' },
          ].map((tab) => (
            <button
              key={tab.key}
              className={cn(
                'px-4 py-2 rounded-md text-[13px] font-medium transition-colors',
                'text-grey-500 hover:text-grey-700 dark:hover:text-grey-300',
                activeTab === tab.key && 'bg-grey-200 dark:bg-grey-700 text-grey-900 dark:text-grey-100'
              )}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto flex flex-col md:flex-row">
          {/* Preview Panel */}
          {activeTab === 'preview' && (
            <div className="flex-1 flex items-center justify-center bg-grey-100 dark:bg-grey-800 relative min-h-[300px] md:min-h-[400px]">
              {previewUrl && isImage ? (
                <>
                  <div className="max-w-full max-h-full overflow-auto flex items-center justify-center p-6">
                    <img
                      src={previewUrl}
                      alt={currentDocument.file_name || 'Document'}
                      className="max-w-full max-h-full object-contain rounded-lg shadow-lg transition-transform duration-200"
                      style={{
                        transform: `scale(${imageZoom}) rotate(${imageRotation}deg)`,
                      }}
                    />
                  </div>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 p-2 bg-white dark:bg-grey-800 rounded-lg shadow-lg">
                    <Button variant="ghost" size="icon" onClick={handleZoomOut} title="Zoom out">
                      <ZoomOut size={18} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={handleZoomIn} title="Zoom in">
                      <ZoomIn size={18} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={handleRotate} title="Rotate">
                      <RotateCw size={18} />
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center gap-3 text-grey-500">
                  <FileText size={48} />
                  <span>Preview not available</span>
                  <Button variant="secondary" onClick={handleDownload}>
                    <ExternalLink size={16} className="mr-2" />
                    Open in new tab
                  </Button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'extracted' && (
            <div className="flex-1 p-6 bg-grey-100 dark:bg-grey-800 items-start justify-start">
              <div className="w-full max-w-[600px]">
                {currentDocument.extracted_data && Object.keys(currentDocument.extracted_data).length > 0 ? (
                  <div className="grid gap-3">
                    {Object.entries(currentDocument.extracted_data).map(([key, value]) => (
                      <div key={key} className="flex flex-col gap-0.5">
                        <span className="text-[11px] text-grey-500 uppercase tracking-wide">{key.replace(/_/g, ' ')}</span>
                        <span className="text-[13px] text-grey-900 dark:text-grey-100">{value || '—'}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-grey-500 text-center py-10">
                    No data extracted yet
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'analysis' && (
            <div className="flex-1 p-6 bg-grey-100 dark:bg-grey-800 items-start justify-start">
              <div className="w-full max-w-[600px]">
                {/* Forensic Analysis Results */}
                {currentDocument.extracted_data?._forensics && (
                  <div className="mb-6">
                    <div className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <Shield size={16} className="text-teal-500" />
                      Forensic Analysis
                    </div>
                    <div className="grid gap-3">
                      {/* ELA Results */}
                      {currentDocument.extracted_data._forensics.ela && (() => {
                        const ela = currentDocument.extracted_data._forensics.ela;
                        const elaScore = ela.manipulation_score ?? 0;
                        const elaVariant = elaScore > 60 ? 'destructive' : elaScore > 30 ? 'warning' : 'success';
                        const elaColorClass = elaScore > 60 ? 'text-red-500 border-red-500 bg-red-500/10' : elaScore > 30 ? 'text-amber-500 border-amber-500 bg-amber-500/10' : 'text-green-500 border-green-500 bg-green-500/10';
                        return (
                          <div className="bg-white dark:bg-grey-900 rounded-xl border border-grey-200 dark:border-grey-700 p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-[13px] font-medium">Error Level Analysis (ELA)</span>
                              <Badge variant={elaVariant}>
                                {ela.status === 'skipped' ? 'Skipped' : ela.passed ? 'Passed' : 'Failed'}
                              </Badge>
                            </div>
                            {ela.status === 'completed' && (
                              <>
                                <div className="flex items-center gap-3 mb-2">
                                  <div className={cn(
                                    'w-10 h-10 rounded-full border-[3px] flex items-center justify-center',
                                    elaColorClass
                                  )}>
                                    <span className="text-sm font-bold">{elaScore}</span>
                                  </div>
                                  <div>
                                    <div className="text-xs text-grey-500">Manipulation Score</div>
                                    <div className="text-xs text-grey-600 dark:text-grey-400">
                                      {ela.suspicious_pct?.toFixed(1)}% suspicious area
                                    </div>
                                  </div>
                                </div>
                                {ela.suspicious_regions?.length > 0 && (
                                  <div className="text-[11px] text-grey-500">
                                    {ela.suspicious_regions.length} region(s) flagged
                                  </div>
                                )}
                              </>
                            )}
                            {ela.status === 'skipped' && (
                              <div className="text-xs text-grey-500">{ela.reason}</div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Synthetic Detection Results */}
                      {currentDocument.extracted_data._forensics.synthetic && (() => {
                        const syn = currentDocument.extracted_data._forensics.synthetic;
                        const synVariant = syn.is_synthetic ? 'destructive' : 'success';
                        const synColorClass = syn.is_synthetic ? 'bg-red-500' : 'bg-green-500';
                        const synTextClass = syn.is_synthetic ? 'text-red-500' : 'text-green-500';
                        return (
                          <div className="bg-white dark:bg-grey-900 rounded-xl border border-grey-200 dark:border-grey-700 p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-[13px] font-medium">Synthetic Image Detection</span>
                              <Badge variant={synVariant}>
                                {syn.status === 'skipped' ? 'Skipped' : syn.passed ? 'Authentic' : 'Synthetic'}
                              </Badge>
                            </div>
                            {syn.status === 'completed' && (
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-1.5 rounded-full bg-grey-200 dark:bg-grey-700 overflow-hidden">
                                  <div
                                    className={cn('h-full rounded-full transition-all duration-300', synColorClass)}
                                    style={{ width: `${syn.confidence || 0}%` }}
                                  />
                                </div>
                                <span className={cn('text-xs font-medium', synTextClass)}>
                                  {syn.confidence || 0}%
                                </span>
                              </div>
                            )}
                            {syn.status === 'skipped' && (
                              <div className="text-xs text-grey-500">{syn.reason}</div>
                            )}
                          </div>
                        );
                      })()}

                      {/* ManTraNet Results (when available) */}
                      {currentDocument.extracted_data._forensics.mantranet && (() => {
                        const mt = currentDocument.extracted_data._forensics.mantranet;
                        const mtScore = mt.manipulation_score ?? 0;
                        const mtVariant = mtScore > 50 ? 'destructive' : mtScore > 25 ? 'warning' : 'success';
                        return (
                          <div className="bg-white dark:bg-grey-900 rounded-xl border border-grey-200 dark:border-grey-700 p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-[13px] font-medium">Deep Manipulation Detection</span>
                              <Badge variant={mtVariant}>
                                {mt.status === 'timeout' ? 'Timeout' : mt.passed ? 'Passed' : 'Failed'}
                              </Badge>
                            </div>
                            {mt.status === 'completed' && (
                              <div className="text-xs text-grey-600 dark:text-grey-400">
                                {mt.suspicious_area_pct?.toFixed(1)}% of image flagged (score: {mtScore}/100)
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* AI Analysis */}
                {currentDocument.ai_analysis ? (
                  <>
                    <div className="text-[13px] leading-relaxed text-grey-600 dark:text-grey-400">
                      {currentDocument.ai_analysis.summary ||
                       currentDocument.ai_analysis.notes ||
                       JSON.stringify(currentDocument.ai_analysis, null, 2)}
                    </div>
                    {currentDocument.ai_analysis.confidence && (
                      <div className="flex items-center gap-2 mt-3 p-3 bg-grey-100 dark:bg-grey-800 rounded-lg">
                        <Sparkles size={14} className="text-teal-500" />
                        <span className="text-[13px]">Confidence</span>
                        <div className="flex-1 h-1.5 bg-grey-300 dark:bg-grey-600 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-teal-500 rounded-full"
                            style={{ width: `${currentDocument.ai_analysis.confidence * 100}%` }}
                          />
                        </div>
                        <span className="text-[13px] font-semibold">
                          {Math.round(currentDocument.ai_analysis.confidence * 100)}%
                        </span>
                      </div>
                    )}
                  </>
                ) : !currentDocument.extracted_data?._forensics ? (
                  <div className="text-grey-500 text-center py-10">
                    No AI analysis available yet
                  </div>
                ) : null}
              </div>
            </div>
          )}

          {/* Details sidebar */}
          <div className="w-full md:w-[360px] border-t md:border-t-0 md:border-l border-grey-200 dark:border-grey-700 overflow-y-auto flex-shrink-0">
            {/* Verification checks */}
            {verificationChecks.length > 0 && (
              <div className="p-5 border-b border-grey-200 dark:border-grey-700 last:border-b-0">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-grey-500 mb-3.5">
                  <Shield size={14} />
                  Verification Checks
                </div>
                {verificationChecks.map((check, idx) => (
                  <VerificationCheck
                    key={idx}
                    label={check.label}
                    passed={check.passed}
                    details={check.details}
                  />
                ))}
              </div>
            )}

            {/* Fraud signals */}
            {currentDocument.fraud_signals && currentDocument.fraud_signals.length > 0 && (
              <div className="p-5 border-b border-grey-200 dark:border-grey-700 last:border-b-0">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-amber-500 mb-3.5">
                  <AlertTriangle size={14} />
                  Fraud Signals
                </div>
                {currentDocument.fraud_signals.map((signal, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-2 p-2.5 bg-amber-500/10 border border-amber-500/20 rounded-lg mb-2 last:mb-0 text-[13px] text-amber-600 dark:text-amber-400"
                  >
                    <AlertTriangle
                      size={14}
                      className={cn(
                        'flex-shrink-0 mt-0.5',
                        signal.severity === 'high' ? 'text-red-500' : 'text-amber-500'
                      )}
                    />
                    <div>
                      <div className="text-xs font-medium">
                        {signal.issue || signal.description || signal.type || signal}
                      </div>
                      {signal.details && (
                        <div className="text-[11px] text-grey-500 mt-0.5">{signal.details}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* OCR text */}
            {currentDocument.ocr_text && (
              <div className="p-5 border-b border-grey-200 dark:border-grey-700 last:border-b-0">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-grey-500 mb-3.5">
                  <FileText size={14} />
                  OCR Text
                </div>
                <div className="text-xs font-mono text-grey-600 dark:text-grey-400 bg-grey-100 dark:bg-grey-800 p-3 rounded-lg max-h-[200px] overflow-auto whitespace-pre-wrap">
                  {currentDocument.ocr_text}
                </div>
              </div>
            )}

            {/* Processing info */}
            <div className="p-5 border-b border-grey-200 dark:border-grey-700 last:border-b-0">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-grey-500 mb-3.5">
                <Sparkles size={14} />
                Processing Info
              </div>
              <div className="grid gap-3">
                <div className="flex flex-col gap-0.5">
                  <span className="text-[11px] text-grey-500 uppercase tracking-wide">Document ID</span>
                  <span className="text-[11px] font-mono text-grey-900 dark:text-grey-100">
                    {currentDocument.id}
                  </span>
                </div>
                {currentDocument.processed_at && (
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[11px] text-grey-500 uppercase tracking-wide">Processed</span>
                    <span className="text-[13px] text-grey-900 dark:text-grey-100">{formatDate(currentDocument.processed_at)}</span>
                  </div>
                )}
                {currentDocument.ocr_confidence !== undefined && (
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[11px] text-grey-500 uppercase tracking-wide">OCR Confidence</span>
                    <span className="text-[13px] text-grey-900 dark:text-grey-100">{Math.round(currentDocument.ocr_confidence)}%</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
